import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import type { FetchCampaignCreatorCountsRequest } from '@/entities/campaign';
import type {
  CreatorFilterOption,
  CreatorLabelFilterOption,
  CreatorPlatformFilterOption,
  CreatorTabFilterRequest,
} from '@/features/campaign/ui/campaign-detail/hooks/campaign-detail-state-types';

type Props = {
  filterRequest: CreatorTabFilterRequest | null;
};

export function useCampaignCreatorFilter({ filterRequest }: Props) {
  const [creatorFilter, setCreatorFilter] = useState<CreatorFilterOption>('all');
  const [creatorSearchQuery, setCreatorSearchQuery] = useState('');
  const [creatorPlatformFilter, setCreatorPlatformFilter] =
    useState<CreatorPlatformFilterOption>(null);
  const [creatorLabelFilter, setCreatorLabelFilter] = useState<CreatorLabelFilterOption>(null);
  const deferredCreatorSearchQuery = useDeferredValue(creatorSearchQuery.trim().toLowerCase());
  const normalizedCreatorSearchQuery = deferredCreatorSearchQuery || undefined;

  useEffect(() => {
    if (!filterRequest) return;

    setCreatorFilter(filterRequest.filter);
  }, [filterRequest]);

  const campaignCreatorRequestParams = useMemo<FetchCampaignCreatorCountsRequest>(
    () => ({
      platform: creatorPlatformFilter ?? undefined,
      label: creatorLabelFilter === null ? undefined : creatorLabelFilter,
      name: normalizedCreatorSearchQuery,
    }),
    [creatorLabelFilter, creatorPlatformFilter, normalizedCreatorSearchQuery],
  );

  const paginationResetKey = useMemo(
    () => [creatorFilter, creatorLabelFilter, creatorPlatformFilter, normalizedCreatorSearchQuery],
    [creatorFilter, creatorLabelFilter, creatorPlatformFilter, normalizedCreatorSearchQuery],
  );

  return {
    campaignCreatorRequestParams,
    creatorFilter,
    creatorLabelFilter,
    creatorPlatformFilter,
    creatorSearchQuery,
    isPendingCreatorFilter: creatorFilter === 'pending',
    normalizedCreatorSearchQuery,
    paginationResetKey,
    setCreatorFilter,
    setCreatorLabelFilter,
    setCreatorPlatformFilter,
    setCreatorSearchQuery,
  };
}
